import {
    IUserMe,
} from 'entities/users/types';

import {
    useCallback,
    useEffect,
} from 'react';
import {
    atom,
    useRecoilState,
} from 'recoil';

import {
    useInterval,
} from 'tools/hooks';

import {
    PERMISSION,
} from 'entities/users/constants';
import {
    hasPermissionOrAdmin,
} from 'entities/users/functions';
import {
    useUserByMe,
} from 'entities/users/hooks';

import {
    ACCOUNT_DRAFT_STATUS,
} from '../constants';
import {
    getAccountDraftsFetch,
} from '../fetches/get-account-drafts.fetch';

const NEW_ACCOUNT_DRAFTS_COUNT_POLLING_DELAY = 60 * 1000;

const newAccountDraftsCountAtom = atom<number | null>({
    key: 'newAccountDraftsCountAtom',
    default: null,
});

async function loadNewAccountDraftsCount(
    authUser: IUserMe | null,
    setCount: (count: number | null) => void
): Promise<void> {
    if (!hasPermissionOrAdmin(authUser, PERMISSION.ACCOUNT_DRAFTS_MANAGE)) {
        setCount(null);

        return;
    }

    const res = await getAccountDraftsFetch({
        id: undefined,
        agentName: undefined,
        type: undefined,
        text: undefined,
        status: [ACCOUNT_DRAFT_STATUS.NEW],
        skip: 0,
        take: 1,
    });

    if (res.error || res.total === undefined) {
        return;
    }

    setCount(res.total);
}

export function useNewAccountDraftsCount() {
    const {authUser} = useUserByMe();

    const [newAccountDraftsCount, setNewAccountDraftsCount] = useRecoilState(newAccountDraftsCountAtom);

    const refresh = useCallback(() => {
        return loadNewAccountDraftsCount(authUser, setNewAccountDraftsCount);
    }, [authUser, setNewAccountDraftsCount]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useInterval(async () => {
        await refresh();
    }, NEW_ACCOUNT_DRAFTS_COUNT_POLLING_DELAY);

    return {
        newAccountDraftsCount,
        refreshNewAccountDraftsCount: refresh,
    };
}
